import { DataSourceError } from "./dataSource";

export interface HttpOptions {
  baseUrl: string;
  apiKey?: string;
  /** Development auth (platform started with SLT_DEV_AUTH=1): tenant slug, and optionally a principal's external id. */
  devTenant?: string;
  devPrincipal?: string;
  /**
   * A signed-in person's token, asked for on every request because it is
   * renewed while the session runs. Wins over `apiKey`: a build with a
   * provider is acting as the person, not as a service.
   */
  token?: () => Promise<string | undefined>;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/**
 * The one place that speaks HTTP to the platform. Adds credentials, applies a
 * timeout, and turns non-2xx into a DataSourceError carrying the platform's
 * `detail` so a refusal ("allowed: dispatched, on_hold") reaches the screen
 * verbatim.
 */
export class HttpClient {
  private readonly base: string;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;
  private readonly headers: Record<string, string>;
  private readonly token?: () => Promise<string | undefined>;

  constructor(opts: HttpOptions) {
    this.base = opts.baseUrl.replace(/\/+$/, "");
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
    this.timeoutMs = opts.timeoutMs ?? 15_000;
    this.headers = { Accept: "application/json" };
    // core/auth.py `identity`: a Bearer credential wins; otherwise, when the
    // platform runs with SLT_DEV_AUTH, X-SLT-Tenant (+ X-SLT-Principal) names
    // the caller. Both are sent when both are configured so the same .env
    // works against a dev platform and a keyed one.
    if (opts.apiKey) this.headers.Authorization = `Bearer ${opts.apiKey}`;
    if (opts.devTenant) this.headers["X-SLT-Tenant"] = opts.devTenant;
    if (opts.devPrincipal) this.headers["X-SLT-Principal"] = opts.devPrincipal;
    this.token = opts.token;
  }

  private async credentials(): Promise<Record<string, string>> {
    if (!this.token) return { ...this.headers };
    const token = await this.token();
    return token ? { ...this.headers, Authorization: `Bearer ${token}` } : { ...this.headers };
  }

  get<T>(path: string, query?: Record<string, string | number | string[] | undefined>) {
    return this.request<T>("GET", path, { query });
  }

  post<T>(path: string, body?: unknown, idempotencyKey?: string, signal?: AbortSignal) {
    return this.request<T>("POST", path, { body, idempotencyKey, signal });
  }

  /** The bytes of a response, with the same credentials — for a file the browser should open or save. */
  async blob(path: string): Promise<Blob> {
    const url = new URL(this.base + path, globalThis.location?.href);
    const res = await this.fetchImpl(url, { headers: await this.credentials() });
    if (!res.ok) throw new DataSourceError(describe(res.status, await res.text()), { status: res.status });
    return res.blob();
  }

  put<T>(path: string, body?: unknown) {
    return this.request<T>("PUT", path, { body });
  }

  patch<T>(path: string, body?: unknown) {
    return this.request<T>("PATCH", path, { body });
  }

  delete<T>(path: string) {
    return this.request<T>("DELETE", path, {});
  }

  /** multipart/form-data — the browser sets the boundary, so no Content-Type is written here. */
  postForm<T>(path: string, form: FormData, idempotencyKey?: string) {
    return this.request<T>("POST", path, { form, idempotencyKey });
  }

  private async request<T>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    opts: {
      query?: Record<string, string | number | string[] | undefined>;
      body?: unknown;
      form?: FormData;
      idempotencyKey?: string;
      /** The caller's own cancellation — a question withdrawn, a screen left. */
      signal?: AbortSignal;
      /** Set on the one retry after a 503, so it is never retried twice. */
      retried?: boolean;
    },
  ): Promise<T> {
    /* Resolved against the page, so a base of "/" means "wherever this app was
       loaded from". That is what lets a phone on the same network use the dev
       server's proxy: an absolute `http://localhost:8000` sent from a phone
       points at the phone. An absolute base ignores the second argument, so
       every existing configuration behaves exactly as before. */
    const url = new URL(this.base + path, globalThis.location?.href);
    for (const [k, v] of Object.entries(opts.query ?? {})) {
      if (v == null) continue;
      if (Array.isArray(v)) v.forEach((x) => url.searchParams.append(k, x));
      else url.searchParams.set(k, String(v));
    }
    const headers = await this.credentials();
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";
    if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), this.timeoutMs);
    /* The caller's signal and the timeout both end the same request, and the
       two are told apart below: a withdrawn question is not a timeout, and
       reporting it as one puts a fault on the screen for something the
       person did on purpose. */
    const abandon = () => ctl.abort();
    opts.signal?.addEventListener("abort", abandon, { once: true });
    let res: Response;
    try {
      res = await this.fetchImpl(url, {
        method,
        headers,
        body: opts.form ?? (opts.body === undefined ? undefined : JSON.stringify(opts.body)),
        signal: ctl.signal,
      });
    } catch (cause) {
      if (opts.signal?.aborted) throw new DataSourceError(`Withdrawn: ${method} ${path}`, { cause });
      throw new DataSourceError(
        ctl.signal.aborted
          ? `Timed out after ${this.timeoutMs} ms: ${method} ${path}`
          : `Network error: ${method} ${path}`,
        { cause },
      );
    } finally {
      clearTimeout(timer);
      opts.signal?.removeEventListener("abort", abandon);
    }

    /*
     * Busy, not broken: every database connection was in use. A read is
     * tried once more after the wait the platform asks for; a write is not,
     * because doing something twice is worse than being told to press again.
     */
    if (res.status === 503 && method === "GET" && !opts.retried) {
      const wait = Math.min(3, Number(res.headers.get("Retry-After")) || 1) * 1000;
      await new Promise((r) => setTimeout(r, wait));
      return this.request<T>(method, path, { ...opts, retried: true });
    }

    const text = await res.text();
    const json: unknown = text ? safeJson(text) : undefined;
    if (!res.ok) {
      const detail = (json as { detail?: unknown } | undefined)?.detail ?? json ?? text;
      throw new DataSourceError(describe(res.status, detail), { status: res.status, detail });
    }
    return json as T;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function describe(status: number, detail: unknown): string {
  const d = typeof detail === "string" ? detail : detail ? JSON.stringify(detail) : "";
  if (status === 401) return `Not authenticated${d ? `: ${d}` : ""}`;
  if (status === 403) return `Not permitted${d ? `: ${d}` : ""}`;
  if (status === 404) return `Not found${d ? `: ${d}` : ""}`;
  if (status === 409 || status === 422) return d || `Refused (${status})`;
  return d ? `${status}: ${d}` : `Request failed (${status})`;
}
